/**
 * Permissions.
 *
 * IMPORTANT: nothing in this file secures anything. The database does that —
 * see supabase/migrations/0005_rls.sql, where every one of these rules is
 * enforced again in Row Level Security.
 *
 * This module exists so the UI can hide what a user cannot do, rather than
 * showing them a button that fails. If the two ever disagree, the database is
 * right and this file is a bug.
 */

/** Mirrors the permission_key enum in 0001_tenancy.sql. */
export const PERMISSIONS = [
  'add_client_payment',
  'add_material_purchase',
  'add_material_usage',
  'add_subcontractor_labour',
  'add_expense',
  'add_stock_transfer',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

export type UserRole = 'admin' | 'staff';

/** What the admin sees on the "create staff" screen, in the order shown. */
export const PERMISSION_LABELS: Record<Permission, { label: string; description: string }> = {
  add_client_payment: {
    label: 'Record client payments',
    description: 'Log money received from the client for a project',
  },
  add_material_purchase: {
    label: 'Record material purchases',
    description: 'Log deliveries received on site against a supplier',
  },
  add_material_usage: {
    label: 'Record material used',
    description: 'Log what was consumed on site',
  },
  add_subcontractor_labour: {
    // Matches the "Add Attendance" button on the project workspace. The two
    // must read the same, or an admin ticks a box and cannot tell which button
    // it turned on.
    label: 'Record attendance',
    description: 'How many workers of each trade a sub-contractor sent today',
  },
  add_expense: {
    label: 'Record site expenses',
    description: 'Vehicle charges, driver charges and other small site spend',
  },
  add_stock_transfer: {
    label: 'Record stock transfers',
    description: 'Move material from this site to another',
  },
};

/** Who the current user is, as far as authorisation is concerned. */
export interface Actor {
  id: string;
  companyId: string;
  fullName: string;
  email: string;
  role: UserRole;
  /** Project ids granted to this user. Ignored for admins. */
  projectIds: readonly string[];
  permissions: readonly Permission[];
}

export function isAdmin(actor: Actor | null | undefined): boolean {
  return actor?.role === 'admin';
}

/** Admins reach every project in their company; staff reach only what was granted. */
export function canAccessProject(actor: Actor | null | undefined, projectId: string): boolean {
  if (!actor) return false;
  if (isAdmin(actor)) return true;
  return actor.projectIds.includes(projectId);
}

/** Permissions that can be granted to site staff/engineers (client payment recording is strictly admin only). */
export const STAFF_PERMISSIONS = PERMISSIONS.filter((p) => p !== 'add_client_payment');

export function hasPermission(actor: Actor | null | undefined, permission: Permission): boolean {
  if (!actor) return false;
  if (isAdmin(actor)) return true;
  // Non-admins (site engineers, staff, etc.) can never record client payments
  if (permission === 'add_client_payment') return false;
  return actor.permissions.includes(permission);
}

/**
 * The complete test for "may this person add this kind of entry to this
 * project" — both halves, the way the RLS INSERT policy checks it.
 */
export function canSubmit(
  actor: Actor | null | undefined,
  permission: Permission,
  projectId: string,
): boolean {
  return canAccessProject(actor, projectId) && hasPermission(actor, permission);
}

/**
 * Staff never edit or delete anything, including their own submissions. This is
 * the product's central promise, so it is a named function rather than an
 * inline role check scattered through the UI.
 */
export function canEditRecords(actor: Actor | null | undefined): boolean {
  return isAdmin(actor);
}

export function canDeleteRecords(actor: Actor | null | undefined): boolean {
  return isAdmin(actor);
}

export function canApprove(actor: Actor | null | undefined): boolean {
  return isAdmin(actor);
}

export function canManageStaff(actor: Actor | null | undefined): boolean {
  return isAdmin(actor);
}

/** Commercial data — supplier rates, contracts, company bank accounts. */
export function canSeeCommercials(actor: Actor | null | undefined): boolean {
  return isAdmin(actor);
}
